/**
 * `SHELL-002`. A non-blocking notice that boot found something wrong with the
 * save document -- rendered above the app, never in place of it, since the
 * whole point is that play continues.
 *
 * Announced once through the shared live region (`platform/announce.ts`)
 * rather than marked up as its own `aria-live` region: that file's own header
 * is explicit that a second simultaneous live region produces overlapping or
 * dropped announcements, and this is exactly the kind of one-off component
 * that would otherwise reach for its own.
 */

import { useEffect } from 'preact/hooks';

import type { LoadNotice } from '@platform/storage';
import { announce } from '@platform/announce';

const MESSAGES: Record<LoadNotice['kind'], string> = {
  unreadable:
    'Previous progress could not be read and has been reset. The old data was kept in case this can be recovered later.',
  'too-new':
    'This save was written by a newer version of Puzzle Gate. Progress here will not be changed until it is updated.',
  // `writable: false` for this one too (storage.ts): the slow read may still
  // land after boot has moved on, so the message says "won't be saved" rather
  // than "has been reset" -- unlike `unreadable`, nothing here is actually
  // known to be wrong with the previous save.
  timeout:
    'Progress is taking longer than usual to load. Play will continue, but nothing will be saved this session.',
};

export function SaveNotice({ notice }: { notice: LoadNotice }) {
  const message = MESSAGES[notice.kind];

  useEffect(() => {
    announce(message);
  }, [message]);

  return <p class="save-notice">{message}</p>;
}
